import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { reportsService } from '../../../services/reportsService';
import { useAuth } from '../../../contexts/AuthContext';

const AIInsightsPanel = () => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadInsights();
    }
  }, [user]);

  const loadInsights = async () => {
    if (!user) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Get user's organization
      const organizationId = user?.user_metadata?.organization_id || 'f47ac10b-58cc-4372-a567-0e02b2c3d479';
      
      // Get financial overview to generate insights
      const financialOverview = await reportsService?.getFinancialOverview(organizationId);
      
      // Generate insights based on actual data
      const generatedInsights = [];

      // Cash Flow Analysis
      if (financialOverview?.cashFlow !== undefined) {
        if (financialOverview?.cashFlow > 0) {
          generatedInsights?.push({
            id: 'cash-flow-positive',
            type: 'success',
            title: 'Positive Cash Flow',
            insight: `Your business has a positive cash flow of $${financialOverview?.cashFlow?.toLocaleString()}. This indicates healthy liquidity.`,
            action: 'Consider investing excess cash or paying down debt',
            priority: 'medium',
            category: 'Cash Flow'
          });
        } else if (financialOverview?.cashFlow < 0) {
          generatedInsights?.push({
            id: 'cash-flow-negative',
            type: 'warning',
            title: 'Negative Cash Flow',
            insight: `Your business has a negative cash flow of $${Math.abs(financialOverview?.cashFlow)?.toLocaleString()}. This requires attention.`,
            action: 'Review accounts receivable and payment terms',
            priority: 'high',
            category: 'Cash Flow'
          });
        }
      }

      // Revenue vs Expenses Analysis
      if (financialOverview?.totalRevenue && financialOverview?.totalExpenses) {
        const profitMargin = ((financialOverview?.totalRevenue - financialOverview?.totalExpenses) / financialOverview?.totalRevenue) * 100;
        
        if (profitMargin > 20) {
          generatedInsights?.push({
            id: 'profit-margin-good',
            type: 'success',
            title: 'Strong Profit Margin',
            insight: `Your profit margin is ${profitMargin?.toFixed(1)}%, which is above industry average.`,
            action: 'Maintain current operational efficiency',
            priority: 'low',
            category: 'Profitability'
          });
        } else if (profitMargin < 5) {
          generatedInsights?.push({
            id: 'profit-margin-low',
            type: 'error',
            title: 'Low Profit Margin',
            insight: `Your profit margin is ${profitMargin?.toFixed(1)}%, which may indicate pricing or cost issues.`,
            action: 'Review pricing strategy and reduce costs',
            priority: 'high',
            category: 'Profitability'
          });
        }
      }

      // Outstanding Balance Analysis
      if (financialOverview?.totalOutstanding > 0) {
        const outstandingRatio = (financialOverview?.totalOutstanding / financialOverview?.totalRevenue) * 100;
        
        if (outstandingRatio > 30) {
          generatedInsights?.push({
            id: 'outstanding-high',
            type: 'warning',
            title: 'High Outstanding Balance',
            insight: `${outstandingRatio?.toFixed(1)}% of your revenue is outstanding. This may impact cash flow.`,
            action: 'Implement stricter payment terms and follow-up procedures',
            priority: 'high',
            category: 'Collections'
          });
        }
      }

      // General Financial Health
      if (financialOverview?.netIncome > 0) {
        generatedInsights?.push({
          id: 'net-income-positive',
          type: 'success',
          title: 'Profitable Operations',
          insight: `Your business generated $${financialOverview?.netIncome?.toLocaleString()} in net income.`,
          action: 'Continue current strategies and consider expansion',
          priority: 'low',
          category: 'Overall Health'
        });
      }

      // If no data available, show placeholder
      if (generatedInsights?.length === 0) {
        generatedInsights?.push({
          id: 'no-data',
          type: 'info',
          title: 'No Insights Available',
          insight: 'Add financial data to get AI-powered insights about your business performance.',
          action: 'Start by adding invoices, expenses, and payments',
          priority: 'low',
          category: 'Getting Started'
        });
      }

      setInsights(generatedInsights);
    } catch (error) {
      console.error('Failed to load AI insights:', error);
      setError('Failed to load AI insights. Please try again.');
      setInsights([]);
    } finally {
      setLoading(false);
    }
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'success':
        return 'CheckCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'error':
        return 'AlertCircle';
      case 'info':
        return 'Info';
      default:
        return 'Brain';
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'success':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-error';
      case 'info':
        return 'text-primary';
      default:
        return 'text-accent';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-error/10 text-error';
      case 'medium':
        return 'bg-warning/10 text-warning';
      case 'low':
        return 'bg-success/10 text-success';
      default:
        return 'bg-muted/10 text-muted-foreground';
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">AI Insights</h3>
          <Icon name="Brain" size={20} className="text-accent" />
        </div>
        <div className="space-y-4">
          {[...Array(3)]?.map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-muted/50 rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="w-3/4 h-4 bg-muted/50 rounded"></div>
                  <div className="w-full h-3 bg-muted/50 rounded"></div>
                  <div className="w-1/2 h-3 bg-muted/50 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-error/20 rounded-lg p-6">
        <div className="text-center">
          <Icon name="AlertCircle" size={32} className="text-error mx-auto mb-3" />
          <h3 className="text-lg font-medium text-foreground mb-2">Error Loading Insights</h3>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <Button 
            variant="outline" 
            size="sm"
            onClick={loadInsights}
            iconName="RefreshCw"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">AI Insights</h3>
        <div className="flex items-center space-x-2">
          <Icon name="Brain" size={20} className="text-accent" />
          <Button
            variant="ghost"
            size="sm"
            onClick={loadInsights}
            iconName="RefreshCw"
            className="text-accent hover:text-accent/80"
          >
            Refresh
          </Button>
        </div>
      </div>
      {/* Insights List */}
      {insights && insights?.length > 0 ? (
        <div className="space-y-4">
          {insights?.map((insight) => (
            <div
              key={insight?.id}
              className="p-4 rounded-lg border border-border hover:bg-muted/20 transition-colors duration-150"
            >
              <div className="flex items-start space-x-3">
                {/* Insight Icon */}
                <div className={`mt-1 ${getInsightColor(insight?.type)}`}>
                  <Icon name={getInsightIcon(insight?.type)} size={20} />
                </div>

                {/* Insight Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-foreground">
                      {insight?.title}
                    </h4>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(insight?.priority)}`}>
                        {insight?.priority}
                      </span>
                      <span className="px-2 py-1 text-xs bg-muted/50 text-muted-foreground rounded-full">
                        {insight?.category}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-2">
                    {insight?.insight}
                  </p>
                  
                  {insight?.action && (
                    <div className="flex items-center space-x-2">
                      <Icon name="Lightbulb" size={14} className="text-accent" />
                      <span className="text-xs text-accent font-medium">
                        {insight?.action}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Icon name="Brain" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">No Insights Available</h4>
          <p className="text-sm text-muted-foreground mb-4">
            AI insights will appear here as your financial data grows and patterns emerge.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={loadInsights}
            iconName="RefreshCw"
          >
            Generate Insights
          </Button>
        </div>
      )}
      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Powered by AI Analytics</span>
          <span>Last updated: {new Date()?.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

export default AIInsightsPanel;